/**
 * report-result-card — client-side "Open report" CTA derivation (#3793).
 *
 * When the agent builds a report inline (report.init → report.section…), the
 * turn should always END with an explicit "Open report" action instead of
 * trailing off into prose. Rather than asking x-agent to emit a new event, we
 * derive a `result.card` ui-action (kind: 'report') from the turn's
 * activeReportModules registry in runChatStream's 'complete' handler — inside
 * the report-finalize block, BEFORE `ctx.activeReportModules.clear()` (deriving
 * later, inside finalizeAssistantMessage, always saw an empty map — PR #3966
 * review). The existing result-card pipeline then does the rest:
 * assembleFinalizeContent partitions result.card actions into the persisted
 * RESULT_CARDS_MARKER block, ChatBox renders <ReportResultCard> beneath the
 * prose, and clicks route through runCardAction → the `pages.open` affordance.
 *
 * Card policy: only the successful-complete path cards, and only reports that
 * actually rendered content (≥1 section). The abort, disconnect-with-partial,
 * and non-streaming error-fallback paths never card — those reports are
 * partial/errored, so a CTA there would be the dead "Open report" button this
 * helper exists to avoid. Pinned by runChatStream.binding.test.ts (#3793).
 */

import type { ChatUIAction } from '@/lib/api/endpoints/chat'
import type { ActiveReportModuleEntry } from '../useChatStream.types'

/**
 * Build one `result.card` action (kind: 'report') per report module built this
 * turn. `existingActions` lets the caller pass the agent-emitted ui actions so
 * a future agent-authored report card for the same module isn't doubled.
 */
export function deriveReportResultCardActions(
  activeReportModules: ReadonlyMap<string, ActiveReportModuleEntry>,
  existingActions: ReadonlyArray<ChatUIAction | null | undefined> = [],
): ChatUIAction[] {
  const alreadyCarded = new Set<string>()
  for (const action of existingActions) {
    if (!action || (action as { type?: string }).type !== 'result.card') continue
    const card = (action as { card?: { kind?: unknown; data?: { moduleId?: unknown } } }).card
    if (card?.kind === 'report' && typeof card.data?.moduleId === 'string') {
      alreadyCarded.add(card.data.moduleId)
    }
  }

  const actions: ChatUIAction[] = []
  for (const [moduleId, entry] of activeReportModules) {
    if (!Array.isArray(entry.config?.sections) || entry.config.sections.length === 0) continue
    if (alreadyCarded.has(moduleId)) continue
    // `title` is written onto the config by applyReportDirective's report.init
    // handler but isn't part of the declared ReportConfig shape — read it
    // defensively instead of widening the type.
    const rawTitle = (entry.config as { title?: unknown }).title
    const title = typeof rawTitle === 'string' && rawTitle.trim() ? rawTitle : 'Report'
    actions.push({
      type: 'result.card',
      card: { kind: 'report', data: { moduleId, title } },
    } as ChatUIAction)
  }
  return actions
}
